/**
 * REPO PATH:
 *   apps/web/src/components/ReviewerBanner.tsx
 *
 * Sticky bottom-bar banner shown when ?reviewer=1 is in the current URL.
 * Detects via window.location on the client (so it works under any layout
 * without forcing a server-side prop drill).
 *
 * Wired into /app/projects/[id]/layout.tsx so it persists across navigation
 * within /projects/* — the existing layout there already exports
 * generateMetadata(); this banner is added to its returned tree without
 * touching that function.
 */
'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function ReviewerBanner() {
  const [active, setActive]   = useState(false);
  const [hidden, setHidden]   = useState(false);

  useEffect(() => {
    const compute = () => {
      try {
        const sp = new URLSearchParams(window.location.search);
        const persisted = window.sessionStorage.getItem('mcc:reviewerMode');
        const next = sp.get('reviewer') === '1' || persisted === '1';
        if (sp.get('reviewer') === '1') {
          window.sessionStorage.setItem('mcc:reviewerMode', '1');
        }
        setActive(next);
      } catch {
        setActive(false);
      }
    };
    compute();
    // Reactive across SPA navigation
    window.addEventListener('popstate', compute);
    window.addEventListener('mcc:reviewer-mode-change', compute);
    return () => {
      window.removeEventListener('popstate', compute);
      window.removeEventListener('mcc:reviewer-mode-change', compute);
    };
  }, []);

  function exitReviewerMode() {
    try {
      window.sessionStorage.removeItem('mcc:reviewerMode');
      const url = new URL(window.location.href);
      url.searchParams.delete('reviewer');
      window.history.replaceState({}, '', url.toString());
      window.dispatchEvent(new Event('mcc:reviewer-mode-change'));
    } catch {
      /* no-op */
    }
    setActive(false);
  }

  if (!active || hidden) return null;

  return (
    <div
      role="region"
      aria-label="Reviewer mode banner"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        background: 'linear-gradient(90deg, var(--mcc-deep) 0%, var(--mcc-navy) 100%)',
        color: 'white',
        borderTop: '1px solid var(--mcc-deep)',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.18)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .rb-inner { flex-direction: column !important; align-items: stretch !important; gap: 10px !important; padding: 12px 14px !important; }
          .rb-text { font-size: 12px !important; }
          .rb-actions { justify-content: flex-end !important; }
        }
      `}</style>
      <div
        className="rb-inner"
        style={{
          maxWidth: 1400,
          margin: '0 auto',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div className="rb-text" style={{ fontSize: 13, lineHeight: 1.5, color: '#e6f2fb' }}>
          <strong style={{ color: 'white', letterSpacing: '0.3px' }}>Reviewer mode</strong>
          <span style={{ margin: '0 8px', color: '#7ad6ff' }}>·</span>
          focus on these sections:&nbsp;
          <span style={{ color: 'white' }}>
            Project description, Methodology fit, Validation status, AI assessment, Safeguards
          </span>
        </div>
        <div className="rb-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Link
            href="/reviewer-guide#submit-feedback"
            style={{
              textDecoration: 'none',
              backgroundColor: '#7ad6ff',
              color: 'var(--mcc-deep)',
              padding: '8px 16px',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.3px',
              whiteSpace: 'nowrap',
            }}
          >
            Submit feedback
          </Link>
          <button
            type="button"
            onClick={exitReviewerMode}
            style={{
              backgroundColor: 'transparent',
              color: '#cdeeff',
              border: '1px solid rgba(205,238,255,0.4)',
              padding: '7px 14px',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
            }}
            aria-label="Exit reviewer mode"
          >
            Exit reviewer mode
          </button>
        </div>
      </div>
    </div>
  );
}
